import { useState, useCallback, useMemo, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Box, LinearProgress, Typography, ThemeProvider, createTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchBar from "./form";
import ServiceCarousel from "./ServiceCarousel";
import { useCatalogServices } from "./useCatalogServices";
import { usePublicSiteSettings } from "./usePublicSiteSettings";
import baseTheme from "./theme";

/**
 * Public customer quote flow at /quote: address + parcels + horizontal services.
 * Branding (colors, sections) comes from GET /site-settings/public.
 */
export default function HomePage() {
  const { operatorSlug: operatorSlugParam } = useParams();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get("embed") === "1";
  const operatorSlug =
    (operatorSlugParam || searchParams.get("o") || searchParams.get("operator") || "").trim().toLowerCase();
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const { services: catalogServices, loading: catalogLoading } = useCatalogServices(operatorSlug);
  const { homeHero, branding, loading: settingsLoading } = usePublicSiteSettings();

  useEffect(() => {
    setSelectedServiceIds([]);
  }, [operatorSlug]);
  const bottomPad = embed ? 2 : 2;
  const footerPad = embed ? 2 : 3;

  const handleToggleService = useCallback((id) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const pageTheme = useMemo(
    () =>
      createTheme(baseTheme, {
        palette: {
          primary: { main: branding.primaryColor },
          secondary: { main: branding.accentColor },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              contained: {
                background: `linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%), ${branding.primaryColor}`,
                boxShadow: `0 4px 14px ${alpha(branding.primaryColor, 0.35)}`,
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha(branding.primaryColor, 0.42)}`,
                },
              },
            },
          },
        },
      }),
    [branding.primaryColor, branding.accentColor]
  );

  const loading = catalogLoading || settingsLoading;

  return (
    <ThemeProvider theme={pageTheme}>
      {loading ? (
        <Box sx={{ position: "sticky", top: 0, zIndex: 1300 }}>
          <LinearProgress color="primary" sx={{ height: 3 }} />
        </Box>
      ) : null}
      <SearchBar
        selectedServiceIds={selectedServiceIds}
        catalogServices={catalogServices}
        homeHero={homeHero}
        operatorQuoteSlug={operatorSlug}
      />
      {branding.showServiceCarousel ? (
        <ServiceCarousel
          services={catalogServices}
          selectedServiceIds={selectedServiceIds}
          onToggleService={handleToggleService}
        />
      ) : null}
      {branding.footerAttribution ? (
        <Box
          component="footer"
          sx={{
            px: 2,
            py: 3,
            pb: footerPad,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, maxWidth: 560, mx: "auto", display: "block" }}>
            {branding.footerAttribution}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ pb: bottomPad }} />
      )}
    </ThemeProvider>
  );
}
